import { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Dialog,
  Button,
  Stack,
  SnackbarOrigin,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DialogStyles } from "./styles/Dialog.styles";
import Preloader from "src/utils/Preloader";
import { AndroidOutlined, Apple } from "@mui/icons-material";
import { generateQR } from "src/utils/generateQR";
import CustomStepper from "src/components/common/CustomStepper";
import ExternalLinkWarning from "src/components/core/ExternalLinkWarning";
import SmsPhoneVerification from "src/components/settings/security/SmsPhoneVerification";
import SixDigitsField from "./SixDigitsField";
import { trackEvent } from "src/Amplitude";
import { Link } from "react-router-dom";
import api from "src/store/features/api";
import { FormikHelpers } from "formik";
import { Create2FaResponseDto, Verify2FaResponseDto } from "src/store/features/api/generated";
import SecondaryInputField from "src/components/wallets/Modal/deposit/SecondaryInputField";
import CopyIcon from "src/icons/CopyIcon";
import { CloseButton, StyledSnackbar } from "src/components/wallets/Modal/deposit/StyledDeposit";
import CrossIcon from "src/icons/CrossIcon";

interface State extends SnackbarOrigin {
  open: boolean;
}

export interface ActivationDialogProps {
  open: boolean;
  selectedValue: string;
  onClose: (value: string) => void;
}

type Options = "sms2fa" | "normal" | "success";

export function ActivationDialog(props: ActivationDialogProps) {
  const { onClose, selectedValue, open } = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.only("xs"));

  const [qrCode, setQRCode] = useState<string>();
  const [mfaSecret, setMfaSecret] = useState<string>();
  const [error, setError] = useState<string>("");
  const [dialogState, setDialogState] = useState<Options>("sms2fa");
  const classes = DialogStyles();
  const [selectedUrl, setSelectedUrl] = useState("");
  const [externalWarning, setExternalWarning] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [snackBarstate, setSnackbarState] = useState<State>({
    open: false,
    vertical: "bottom",
    horizontal: "left",
  });

  const handleClose = () => {
    onClose(selectedValue);
    setDialogState("sms2fa");
  };

  const mobileWarningMessage1 =
    "To access certain features in the C&B Portal we require you first setup Two Factor Authentication (2FA).";
  const onYourPhone = "on your phone";
  const mobileWarningMessage2 = "This requires an Authenticator app installed ";
  const mobileWarningMessage3 =
    ", if you don't have one of these apps click on the link below to download the Google Authenticator app.";
  const [validateTwoFA] = api.useVerify2FaMutation();
  const [enableTwoFA] = api.useLazyCreate2FaQuery();
  const { refetch } = api.useGetUserDatav2Query();

  useEffect(() => {
    function getQrCode() {
      enableTwoFA()
        .unwrap()
        .then(async (res: Create2FaResponseDto) => {
          const uri = res.uri;
          setMfaSecret(res.secret);

          generateQR(uri).then((imageData) => {
            if (typeof imageData !== "undefined") setQRCode(imageData);
          });
        });
    }
    if (dialogState === "normal") getQrCode();
    return () => {
      setQRCode(undefined);
    };
  }, [dialogState]);

  const handleExternalLink = (type: string) => {
    setExternalWarning(true);
    switch (type) {
      case "apple":
        setSelectedUrl("https://apps.apple.com/au/app/google-authenticator/id388497605");

        break;
      case "google":
        setSelectedUrl(
          "https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&hl=en_AU&gl=US",
        );
        break;
    }
  };

  const handleSubmit = (
    body: { code: string },
    actions: FormikHelpers<{
      code: string;
    }>,
  ) => {
    setShowProgress(true);
    validateTwoFA({ twoFactorDto: body })
      .unwrap()
      .then((res: Verify2FaResponseDto) => {
        const status = res.factorStatus;
        setShowProgress(false);
        if (status === "unverified") return setError("Invalid code, please try again");
        setDialogState("success");
        trackEvent("2FA Successfully", {
          option: "Switched ON",
        });
      })
      .catch(() => {
        setError("Invalid Code, please try again");
        setShowProgress(false);
      })
      .finally(() => {
        actions.setSubmitting(false);
        refetch();
      });
  };

  const setUserSelectionCallBack = (permission: string) => {
    if (permission === "Confirm") return window.open(selectedUrl, "_blank");
  };

  // copy address to clipboard and activate tooltip
  const copyToClipboard = (text: string): void => {
    navigator.clipboard.writeText(text);
    setSnackbarState({ ...snackBarstate, open: true });
    setTimeout((): void => {
      setSnackbarState({ ...snackBarstate, open: false });
    }, 1000);
  };

  return (
    <>
      <ExternalLinkWarning
        option={externalWarning}
        setExternalWarning={setExternalWarning}
        handleSelection={setUserSelectionCallBack}
        selectedUrl={selectedUrl}
      />
      <StyledSnackbar
        anchorOrigin={{ vertical: snackBarstate.vertical, horizontal: snackBarstate.horizontal }}
        open={snackBarstate.open}
        onClose={() => setSnackbarState({ ...snackBarstate, open: false })}
        autoHideDuration={1000}
        message={"Copied to clipboard"}
        key={snackBarstate.vertical + snackBarstate.horizontal}
        action={
          <CloseButton
            size="small"
            onClick={() => setSnackbarState({ ...snackBarstate, open: false })}>
            <CrossIcon height={"15"} width={"15"} />
          </CloseButton>
        }
      />
      {dialogState === "sms2fa" && (
        <Dialog onClose={handleClose} open={open}>
          <SmsPhoneVerification setDialogState={setDialogState} />
        </Dialog>
      )}
      {dialogState === "normal" && (
        <Dialog onClose={handleClose} open={open}>
          <Grid container className={classes.container}>
            <Grid container item xs={12} pb={2}>
              <CustomStepper type={"sms"} activeStep={2} />
            </Grid>
            {/* Mobile 2FA */}
            <Grid container>
              {/* MOBILE TITLE */}
              <Grid item xs={12}>
                <Typography
                  style={{
                    fontSize: "20px",
                    fontWeight: 600,
                  }}>
                  {"Setup 2FA"}
                </Typography>
              </Grid>
              {/* MOBILE WARNING MESSSAGES */}
              <Grid item xs={12} py={2}>
                <Typography mb={2}>{mobileWarningMessage1}</Typography>
                <Typography display="inline">{mobileWarningMessage2}</Typography>
                <Typography display="inline" style={{ fontWeight: "bold" }}>
                  {onYourPhone}
                </Typography>
                <Typography display="inline">{mobileWarningMessage3}</Typography>
              </Grid>
              {/* BUTTONS */}
              <Grid container spacing={2} mb={2} textAlign={"center"}>
                <Grid item xs={6}>
                  {/* APP STORE BUTTON */}
                  <Button
                    disableRipple
                    fullWidth
                    size={"large"}
                    onClick={() => {
                      handleExternalLink("apple");
                    }}
                    variant="outlined"
                    startIcon={<Apple />}>
                    {"iOS"}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  {/* GOOGLE PLAY BUTTON */}
                  <Button
                    disableRipple
                    fullWidth
                    size={"large"}
                    variant="outlined"
                    startIcon={<AndroidOutlined />}
                    onClick={() => {
                      handleExternalLink("google");
                    }}>
                    {"Android"}
                  </Button>
                </Grid>
                {/* QR CODE CONTENT */}
                <Grid item xs={12} className={classes.qrCodeContainer}>
                  <Grid item container xs={12} mt={1}>
                    {"Open the Authenticator app and scan the QR code below"}
                  </Grid>
                  <Grid item xs={12}>
                    {!qrCode && (
                      <Grid mt={4}>
                        <Preloader />
                      </Grid>
                    )}
                    {qrCode && <img src={qrCode} alt={"..."} width="220" height="220" />}
                  </Grid>
                </Grid>
                {/* 2FA SECRET CONTENT */}
                {isSmallScreen && (
                  <Grid item xs={12}>
                    <Grid item container xs={12} mt={1}>
                      {"Copy the setup code below to use in your Authenticator app"}
                    </Grid>
                    <Grid item xs={12}>
                      {!mfaSecret && (
                        <Grid mt={4}>
                          <Preloader />
                        </Grid>
                      )}
                      {mfaSecret && (
                        <SecondaryInputField
                          disabled={true}
                          defaultValue={mfaSecret}
                          inputType="text"
                          id="2fa-secret-input"
                          endAdornment={
                            true && (
                              <>
                                <Button
                                  onClick={(): void => copyToClipboard(mfaSecret)}
                                  endIcon={<CopyIcon />}
                                  sx={{
                                    backgroundColor: theme.palette.secondary.main,
                                    color: "#fff",
                                    textTransform: "capitalize",
                                    paddingRight: "15px",
                                    paddingLeft: "15px",
                                  }}>
                                  Copy
                                </Button>
                              </>
                            )
                          }
                          sx={{
                            width: "100%",
                            backgroundColor: "#F8F9F9",
                            fontWeight: 700,
                            textOverflow: "clip ellipsis clip 0 3ch",
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* INPUT FIELDS */}
            <SixDigitsField
              handleSubmit={handleSubmit}
              error={error}
              setError={setError}
              onClose={handleClose}
              showProgress={showProgress}
            />
          </Grid>
        </Dialog>
      )}
      {dialogState === "success" && (
        <Dialog onClose={handleClose} open={open}>
          <Grid container className={classes.container} textAlign="center">
            <CustomStepper type={"sms"} activeStep={3} />
            <Stack mt={4}>
              <Typography className={classes.textField}>{"Success! "}</Typography>
              <Typography>
                You can now start trading{" "}
                <Link
                  to="/trade"
                  style={{
                    textDecoration: "none",
                    fontWeight: "bold",
                    color: "#fc4c4c",
                  }}>
                  here.
                </Link>
              </Typography>
            </Stack>
          </Grid>
        </Dialog>
      )}
    </>
  );
}
